import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import type { QueryClient } from "@tanstack/react-query";

// Pages feel instant when their data is already here before the click.
// Two ways it gets here:
//  1. Route memory: while a page is open we note which queries it used.
//     Hovering its link later refreshes the ones that went stale.
//  2. Warmers: the main pages register how to fetch their first screen,
//     so even the first visit of a session can start loading on hover.

const byPath = new Map<string, Set<string>>();
export interface WarmCtx { orgId: string; base: string; allows: (p: string) => boolean }
const warmers = new Map<string, (qc: QueryClient, ctx: WarmCtx) => void>();
let current = "";

const norm = (p: string) => p.split(/[?#]/)[0].replace(/\/+$/, "") || "/";

/** setCurrentPath tells route memory which page is on screen. */
export function setCurrentPath(p: string) { current = norm(p); }

/** rememberQueries records, per page, the queries it watched. Call once. */
export function rememberQueries(qc: QueryClient) {
  return qc.getQueryCache().subscribe((e) => {
    if (e.type !== "observerAdded" || !current) return;
    if (e.observer.options.enabled === false) return;
    let s = byPath.get(current);
    if (!s) byPath.set(current, (s = new Set()));
    if (s.size < 40) s.add(e.query.queryHash);
  });
}

/** registerWarmer lets a page say how to fetch its first screen. `section` is the path after /o/:org. */
export function registerWarmer(section: string, fn: (qc: QueryClient, ctx: WarmCtx) => void) { warmers.set(section, fn); }

/** sectionOf is the path after /o/:orgId (the warmers' key), or null outside a company. */
export function sectionOf(path: string) {
  const m = norm(path).match(/^\/o\/[^/]+(?:\/(.*))?$/);
  return m ? (m[1] ?? "") : null;
}

/** warm starts loading whatever the page at `path` will need. Safe to call often. */
export function warm(qc: QueryClient, path: string, ctx?: WarmCtx) {
  const p = norm(path);
  const seen = byPath.get(p);
  if (seen && seen.size) {
    for (const h of seen) {
      const q = qc.getQueryCache().get(h);
      if (q && q.options.queryFn && q.state.fetchStatus === "idle" && q.isStale()) q.fetch().catch(() => { /* the page shows its own error */ });
    }
    return;
  }
  const s = sectionOf(p);
  if (s != null && ctx) {
    try { warmers.get(s)?.(qc, ctx); } catch { /* warming is best effort */ }
  }
}

/** hoverWarm returns props for a link: hovering (with intent) or focusing it warms the page. */
export function hoverWarm(qc: QueryClient, path: string, ctx?: WarmCtx) {
  let t: number | undefined;
  return {
    onPointerEnter: () => { t = window.setTimeout(() => warm(qc, path, ctx), 60); },
    onPointerLeave: () => { window.clearTimeout(t); },
    onFocus: () => warm(qc, path, ctx),
    onTouchStart: () => warm(qc, path, ctx),
  };
}

/** useIdleWarm warms the given pages once, when the browser is idle after the app opens. */
export function useIdleWarm(qc: QueryClient, paths: string[], ctx: WarmCtx) {
  const done = useRef(false);
  useEffect(() => {
    if (done.current || paths.length === 0) return;
    done.current = true;
    const run = () => paths.forEach((p, i) => window.setTimeout(() => warm(qc, p, ctx), i * 150));
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
    const id = ric ? ric(run, { timeout: 4000 }) : window.setTimeout(run, 2500);
    return () => { if (!ric) window.clearTimeout(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paths.join("|")]);
}

/**
 * useNavGlide moves one highlight (the blue bar and the raised pill, as on
 * svenex.app) to the active link inside `ref`, sliding between pages.
 */
export function useNavGlide(ref: RefObject<HTMLElement>, deps: unknown[]) {
  const ready = useRef(false);
  useLayoutEffect(() => {
    const box = ref.current;
    if (!box) return;
    const glide = box.querySelector<HTMLElement>(":scope > .nav-glide");
    if (!glide) return;
    const place = () => {
      const a = box.querySelector<HTMLElement>("a.active");
      if (!a || a.offsetParent === null) { glide.style.opacity = "0"; return; }
      const br = box.getBoundingClientRect(), ar = a.getBoundingClientRect();
      glide.style.transform = `translate3d(${ar.left - br.left}px, ${ar.top - br.top + box.scrollTop}px, 0)`;
      glide.style.width = `${ar.width}px`;
      glide.style.height = `${ar.height}px`;
      glide.style.opacity = "1";
    };
    if (!ready.current) {
      // First placement: no slide in from the top.
      glide.style.transition = "none";
      place();
      void glide.offsetWidth;
      glide.style.transition = "";
      ready.current = true;
    } else {
      place();
    }
    const ro = new ResizeObserver(() => place());
    ro.observe(box);
    Array.from(box.children).forEach((c) => ro.observe(c));
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
